<template>
  <div
    class="appointment-report ar-pmx"
    :aria-busy="mxIsLoading || mxIsFinalizing ? 'true' : 'false'"
  >
    <div class="ar-pmx__body">
      <div
        v-if="isLoadingState"
        class="ar-pmx__loading"
      >
        <svg
          class="ar-spinner"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
        >
          <circle
            cx="12"
            cy="12"
            r="9"
            stroke="currentColor"
            stroke-opacity=".25"
            stroke-width="2.5"
          />
          <path
            d="M21 12a9 9 0 0 0-9-9"
            stroke="currentColor"
            stroke-width="2.5"
            stroke-linecap="round"
          />
        </svg>
      </div>

      <div
        v-else-if="mxStatus === MX_REPORT_STATUS.LOAD_ERROR"
        class="ar-pmx__notice"
      >
        <div
          class="ar-callout ar-callout--danger"
          role="alert"
        >
          <feather-icon
            icon="AlertTriangleIcon"
            size="20"
            aria-hidden="true"
          />
          <p class="ar-pmx__notice-text">
            {{ mxReport.errorMessage || APP_COPY.loadReportError }}
          </p>
        </div>
        <button
          type="button"
          class="ar-btn ar-btn--secondary ar-btn--sm"
          @click="mxLoad()"
        >
          <feather-icon
            icon="RefreshCwIcon"
            size="16"
            aria-hidden="true"
          />
          {{ NEW_COPY.retry }}
        </button>
      </div>

      <div
        v-else-if="mxStatus === MX_REPORT_STATUS.UNAVAILABLE"
        class="ar-pmx__notice"
      >
        <p
          class="ar-callout ar-callout--warning"
          role="alert"
        >
          <feather-icon
            icon="AlertCircleIcon"
            size="16"
            aria-hidden="true"
          />
          <span>{{ mxReport.errorMessage || APP_COPY.notOwner }}</span>
        </p>
      </div>

      <template v-else-if="!mxIsDone">
        <report-date-lock-banner
          v-if="mxShowDateLock"
          class="ar-pmx__date-lock"
          v-bind="mxDateLockProps"
          :show-actions="false"
          @released="mxOnReleased"
        />
        <div class="ar-questions ar-questions--compact">
          <div class="ar-classification">
            <report-reason-select
              :ref="mxItemRef(REPORT_CLASSIFICATION)"
              v-bind="mxClassificationProps()"
              v-on="mxClassificationListeners()"
            />
            <p class="ar-classification__help">
              {{ REPORT_CLASSIFICATION_COPY.help }}
            </p>
          </div>
          <report-yes-no
            v-for="key in mxVisibleQuestions"
            :key="key"
            :ref="mxItemRef(key)"
            v-bind="mxYesNoProps(key)"
            v-on="mxListeners(key)"
          >
            <template #follow-up>
              <report-reason-select
                v-if="mxFollowUpOf(key)"
                :ref="mxItemRef(mxFollowUpOf(key))"
                v-bind="mxReasonProps(mxFollowUpOf(key))"
                v-on="mxListeners(mxFollowUpOf(key))"
              />
            </template>
          </report-yes-no>
        </div>
      </template>
    </div>

    <report-action-bar
      v-if="mxShowActionBar"
      v-bind="mxActionBarProps"
      @finalize="mxFinalize"
      @jump-to-missing="mxJumpToMissing()"
    />

    <span
      class="ar-sr-only"
      aria-live="polite"
    >{{ mxReport.announce }}</span>
  </div>
</template>

<script>
import {
  APP_COPY,
  NEW_COPY,
  REPORT_CLASSIFICATION,
  REPORT_CLASSIFICATION_COPY,
} from '@/constants/appointment_report_v2'
import momentXReportForm, { MX_REPORT_STATUS } from '../../mixins/momentXReportForm'
import ReportActionBar from '../ReportActionBar.vue'
import ReportDateLockBanner from '../ReportDateLockBanner.vue'
import ReportReasonSelect from '../ReportReasonSelect.vue'
import ReportYesNo from '../ReportYesNo.vue'

/**
 * Report de Momento X dentro do painel da central de pendências (board v4-pendencias): as mesmas perguntas,
 * selects e regras do Report (mixin `momentXReportForm`), em layout compacto — linhas baixas, select até 600px,
 * rodapé do painel com status + autosave + "Finalizar Momento X" ("Finalizando…" ≥ 700ms).
 *
 * A central monta um por pendência selecionada (`:key` = id do compromisso). Eventos (do mixin):
 *  - `mx-report-finalized` `{ report, replayed, pending_count }` — gravado; a central marca como resolvida;
 *  - `mx-report-already-reported` `report` — já tinha report (outra aba/dispositivo): também resolvida;
 *  - `mx-report-pending-rejected` `{ appointmentId, pendingIds }` — 423 da trava: saiu das pendências sem report
 *    (cancelado, excluído, transferido); o formulário mostra o aviso e a central tira o item da lista;
 *  - `draft-saved` — o autosave gravou o rascunho (a lista mostra "Rascunho iniciado").
 * A confirmação ("Report salvo" + "Próxima pendência") é da central, não deste componente.
 */
export default {
  name: 'PendencyMomentXForm',
  components: {
    ReportActionBar,
    ReportDateLockBanner,
    ReportReasonSelect,
    ReportYesNo,
  },
  mixins: [momentXReportForm],
  props: {
    appointmentId: {
      type: [Number, String],
      required: true,
    },
  },
  data() {
    return {
      APP_COPY,
      NEW_COPY,
      MX_REPORT_STATUS,
      REPORT_CLASSIFICATION,
      REPORT_CLASSIFICATION_COPY,
      // Layout do painel da central (ver o mixin).
      mxCompact: true,
    }
  },
  computed: {
    isLoadingState() {
      return this.mxIsLoading || this.mxStatus === MX_REPORT_STATUS.IDLE
    },
  },
  watch: {
    appointmentId(id) {
      this.mxInit(id)
    },
    'mxReport.draftSavedAt': function onDraftSaved(savedAt) {
      if (savedAt) this.$emit('draft-saved', { appointmentId: Number(this.appointmentId), savedAt })
    },
  },
  created() {
    this.$_pmxAlreadyReportedEmitted = false
    this.mxInit(this.appointmentId)
  },
  methods: {
    // Gancho do mixin (`mxCallHook`), chamado junto com o evento `mx-report-already-reported`.
    onMomentXReportAlreadyReported() {
      this.$_pmxAlreadyReportedEmitted = true
    },

    /**
     * 409 APPOINTMENT_ALREADY_REPORTED no rascunho/finalize: o mixin busca o report gravado para o resumo e só
     * então emite o evento. Se essa busca falhar (ou vier sem report), o formulário fica "já reportado" sem nada a
     * mostrar e a central esperaria para sempre — então emite mesmo sem o resumo.
     */
    mxHandleAlreadyReported() {
      const id = this.mxReport.appointmentId
      this.$_pmxAlreadyReportedEmitted = false

      return momentXReportForm.methods.mxHandleAlreadyReported.call(this).then(() => {
        if (this.$_mxDestroyed || id !== this.mxReport.appointmentId) return
        if (this.$_pmxAlreadyReportedEmitted || this.mxStatus !== MX_REPORT_STATUS.ALREADY_REPORTED) return
        this.$_pmxAlreadyReportedEmitted = true
        this.$emit('mx-report-already-reported', this.mxReport.report || null)
      })
    },
  },
}
</script>

<style lang="scss" src="@/assets/scss/_appointment-report.scss"></style>

<style lang="scss" scoped>
.ar-pmx {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.ar-pmx__body {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.ar-pmx__loading {
  flex: 1 1 auto;
  min-height: 240px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--ar-text-muted);
}

.ar-pmx__notice {
  padding: 20px 24px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
}

.ar-pmx__notice-text {
  margin: 0;
}

.ar-pmx__date-lock {
  margin: 16px 24px 0;
}

// Rodapé do painel: o painel da central não recorta o conteúdo (a lista do select pode passar da borda), então os
// cantos de baixo do fundo do rodapé acompanham o raio do card aqui.
.ar-pmx ::v-deep .ar-action-bar--pane .ar-action-bar__bar {
  border-radius: 0 0 var(--ar-radius-card) var(--ar-radius-card);
}
</style>
